package com.sweetlime.dungeonamigo.presentation.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.sweetlime.dungeonamigo.presentation.ui.theme.BrandColors
import com.sweetlime.dungeonamigo.util.AvatarMapper

/**
 * Full-screen bottom sheet that lets the user choose any of the 96 base avatars
 * as their profile picture.
 *
 * @param currentAvatarId   Currently selected avatar ID (may be blank).
 * @param onSelect          Called with the chosen avatarId when the user confirms.
 * @param onDismiss         Called when the sheet is dismissed without saving.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AvatarPickerSheet(
    currentAvatarId: String,
    onSelect: (String) -> Unit,
    onDismiss: () -> Unit,
) {
    var selected by remember { mutableStateOf(currentAvatarId) }
    var activeRace by remember { mutableStateOf(AvatarMapper.races.first()) }

    ModalBottomSheet(
        onDismissRequest = onDismiss,
        containerColor = BrandColors.Surface,
        dragHandle = { BottomSheetDefaults.DragHandle(color = BrandColors.TextSecondary) },
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(bottom = 32.dp),
        ) {
            // ── Header ────────────────────────────────────────────────────────
            Text(
                text = "Choose Avatar",
                color = BrandColors.Parchment,
                fontWeight = FontWeight.Bold,
                fontSize = 18.sp,
                modifier = Modifier.padding(horizontal = 20.dp, vertical = 8.dp),
            )

            // ── Race filter tabs ──────────────────────────────────────────────
            LazyRow(
                contentPadding = PaddingValues(horizontal = 16.dp),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                modifier = Modifier.fillMaxWidth().padding(bottom = 12.dp),
            ) {
                items(AvatarMapper.races) { race ->
                    val isActive = race == activeRace
                    Box(
                        modifier = Modifier
                            .clip(RoundedCornerShape(20.dp))
                            .background(
                                if (isActive) BrandColors.Ember
                                else BrandColors.SurfaceLight,
                            )
                            .clickable { activeRace = race }
                            .padding(horizontal = 14.dp, vertical = 6.dp),
                    ) {
                        Text(
                            text = AvatarMapper.raceDisplayNames[race] ?: race,
                            color = if (isActive) BrandColors.TextPrimary else BrandColors.TextSecondary,
                            fontSize = 13.sp,
                            fontWeight = if (isActive) FontWeight.SemiBold else FontWeight.Normal,
                        )
                    }
                }
            }

            // ── Avatar grid ───────────────────────────────────────────────────
            val avatarsForRace = AvatarMapper.classes.map { cls -> "${activeRace}_${cls}" }

            LazyVerticalGrid(
                columns = GridCells.Fixed(3),
                modifier = Modifier
                    .fillMaxWidth()
                    .height(320.dp)
                    .padding(horizontal = 16.dp),
                horizontalArrangement = Arrangement.spacedBy(10.dp),
                verticalArrangement = Arrangement.spacedBy(10.dp),
                contentPadding = PaddingValues(bottom = 8.dp),
            ) {
                items(avatarsForRace) { avatarId ->
                    val isSelected = avatarId == selected
                    AvatarGridTile(
                        avatarId = avatarId,
                        isSelected = isSelected,
                        primaryColor = BrandColors.Parchment,
                        secondaryColor = BrandColors.SpellBlue,
                        onClick = { selected = avatarId },
                    )
                }
            }

            // ── Class label for the selected tile ────────────────────────────
            val selectedClass = selected.removePrefix("${activeRace}_").replace("_", " ")
                .replaceFirstChar { it.uppercase() }
            Text(
                text = if (selected.startsWith(activeRace)) selectedClass else "",
                color = BrandColors.Parchment,
                fontSize = 13.sp,
                fontWeight = FontWeight.Medium,
                modifier = Modifier
                    .align(Alignment.CenterHorizontally)
                    .padding(top = 4.dp),
            )

            // ── Action buttons ────────────────────────────────────────────────
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 20.dp, vertical = 12.dp),
                horizontalArrangement = Arrangement.spacedBy(12.dp),
            ) {
                OutlinedButton(
                    onClick = onDismiss,
                    modifier = Modifier.weight(1f),
                    border = androidx.compose.foundation.BorderStroke(1.dp, BrandColors.SurfaceLight),
                    colors = ButtonDefaults.outlinedButtonColors(contentColor = BrandColors.TextSecondary),
                    shape = RoundedCornerShape(8.dp),
                ) {
                    Text("Cancel")
                }
                Button(
                    onClick = { if (selected.isNotBlank()) onSelect(selected) },
                    modifier = Modifier.weight(1f),
                    enabled = selected.isNotBlank(),
                    colors = ButtonDefaults.buttonColors(containerColor = BrandColors.Ember),
                    shape = RoundedCornerShape(8.dp),
                ) {
                    Text("Save", color = BrandColors.TextPrimary, fontWeight = FontWeight.Bold)
                }
            }
        }
    }
}

@Composable
private fun AvatarGridTile(
    avatarId: String,
    isSelected: Boolean,
    primaryColor: Color,
    secondaryColor: Color,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Box(
        modifier = modifier
            .aspectRatio(1f)
            .clip(RoundedCornerShape(10.dp))
            .clickable(onClick = onClick)
            .then(
                if (isSelected)
                    Modifier.border(2.dp, BrandColors.Ember, RoundedCornerShape(10.dp))
                else
                    Modifier.border(1.dp, BrandColors.SurfaceLight, RoundedCornerShape(10.dp))
            ),
    ) {
        CharacterPortrait(
            characterClass = avatarId.substringAfterLast("_"),
            avatarId = avatarId,
            primaryColor = primaryColor,
            secondaryColor = secondaryColor,
            size = PortraitSize.MEDIUM,
            modifier = Modifier.fillMaxSize(),
        )
        if (isSelected) {
            Box(
                modifier = Modifier
                    .align(Alignment.TopEnd)
                    .padding(4.dp)
                    .size(18.dp)
                    .clip(CircleShape)
                    .background(BrandColors.Ember),
                contentAlignment = Alignment.Center,
            ) {
                Text("✓", color = BrandColors.TextPrimary, fontSize = 11.sp, fontWeight = FontWeight.Bold)
            }
        }
    }
}
